import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom';

import { virtualizedTableColumns } from './virtualizedTableColumns';
import { VirtualizedTableColumn } from './components/VirtualizedTable';
import toastMsg from '../../../lib/toastMsg';
import { asyncSimpleModal } from '../../../lib/common';
import { useOrderItemsExcelUpload } from '../../../hooks/queries/multiChannel/useOrderItemsExcelUpload';
import { OrderExcelUploadItem } from '../../../types/multiChannel';
import { useCreateOrderItems } from '../../../hooks/queries/multiChannel/useCreateOrderItems';
import { convertOrderListToOrderCreateInput } from './utils';
import { MULTI_CHANNEL_ORDER_MANAGEMENT_URL_PATH } from '../../../constants/service/common/urlConstants';

interface OrderCreateViewModel {
  totalCount: number;
  possibleCount: number;
  impossibleCount: number;
  isImpossibleCountVisible: boolean;
  setIsImpossibleCountVisible: (value: boolean) => void;
  checkedIdList: string[];
  setCheckedIdList: (value: string[]) => void;
  deleteOrderItems: () => void;
  createOrder: () => void;
  orderList: OrderExcelUploadItem[];
  columns: VirtualizedTableColumn[];
  uploadExcel: (payload: File) => void;
  tableHeight: number;
  isCreateOrderActive: boolean;
}
const useOrderCreateViewModel = (): OrderCreateViewModel => {
  const [orderList, setOrderList] = useState<OrderExcelUploadItem[]>([]);
  const [isImpossibleCountVisible, setIsImpossibleCountVisible] = useState(false);
  const [checkedIdList, setCheckedIdList] = useState<string[]>([]);
  const columns = virtualizedTableColumns();
  const { mutate: excelUploadMutate } = useOrderItemsExcelUpload();
  const { mutate: createOrderItemsMutate } = useCreateOrderItems();
  const filteredOrderList = isImpossibleCountVisible ? orderList.filter((order) => !order.isValid) : orderList;
  const [tableHeight, setTableHeight] = useState(500);
  const navigate = useNavigate();

  useEffect(() => {
    const resizeTable = (): void => {
      const windowHeight = window.innerHeight;
      setTableHeight(windowHeight - 440);
    };

    resizeTable();
    window.addEventListener('resize', resizeTable);
    return () => window.removeEventListener('resize', resizeTable);
  }, []);

  const getCountInfo = (): {
    totalCount: number;
    possibleCount: number;
    impossibleCount: number;
  } => {
    const totalCount = orderList.length;
    const possibleCount = orderList.filter((order) => order.isValid).length;
    const impossibleCount = totalCount - possibleCount;
    return { totalCount, possibleCount, impossibleCount };
  };

  const deleteOrderItems = async (): Promise<void> => {
    if (checkedIdList.length === 0) {
      toastMsg.error('주문을 선택해주세요.');
      return;
    }

    const confirmResult = await asyncSimpleModal({
      type: 'confirm',
      title: '선택 삭제',
      message: `${checkedIdList.length}개 항목이 리스트에서 삭제 됩니다.`,
    });

    if (!confirmResult) return;

    const newOrderList = orderList.filter((order) => !checkedIdList.includes(order.id));
    setOrderList(newOrderList);
    setCheckedIdList([]);
  };

  const createOrder = (): void => {
    createOrderItemsMutate(convertOrderListToOrderCreateInput(orderList), {
      onSuccess: () => {
        setOrderList([]);
        navigate(MULTI_CHANNEL_ORDER_MANAGEMENT_URL_PATH);
      },
      onError: async () => {
        const confirmResult = await asyncSimpleModal({
          type: 'error',
          title: '생성 불가',
          message: '유효하지 않은 데이터가 있어 목록이 초기화됩니다.\n' + '다시 시도해주세요.',
        });

        if (confirmResult) {
          setOrderList([]);
        }
      },
    });
  };

  const uploadExcel = (payload: File): void => {
    excelUploadMutate(payload, {
      onSuccess: (data) => {
        const orderItems =
          data.multiChannelOrderItems.map((item) => ({
            ...item,
            id: uuidv4(),
          })) || [];
        setOrderList(orderItems);
        setCheckedIdList([]);
      },
    });
  };

  const { totalCount, possibleCount, impossibleCount } = getCountInfo();
  const isCreateOrderActive = totalCount > 0 && possibleCount === totalCount;
  return {
    totalCount,
    possibleCount,
    impossibleCount,
    isImpossibleCountVisible,
    setIsImpossibleCountVisible,
    checkedIdList,
    setCheckedIdList,
    deleteOrderItems,
    createOrder,
    orderList: filteredOrderList,
    columns,
    uploadExcel,
    tableHeight,
    isCreateOrderActive,
  };
};

export default useOrderCreateViewModel;
